/**
 * Extra visits on a job — multi-day work and return trips.
 *
 * The order's own `fop_*` UDFs are the FIRST visit; a `JobVisit` is each
 * visit after it (own day, time, crew), stored in Tinsmith, not Spire. See
 * apps/api/src/modules/visits/visits.service.ts.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  ApiError,
  type CreateJobVisitBody,
  type JobVisit,
  type SalesOrderSummary,
  type UpdateJobVisitBody,
} from './api.js';
import { FOP_UDF, formatStoredDuration, parseStoredStart, readAssignment, readCompletedAt } from './dispatch-board.js';

const VISITS_KEY = ['visits'] as const;

/** Every visit in a date window (the board's view of a day, plus a margin). */
export function useVisitsWindow(from: string, to: string) {
  return useQuery({
    queryKey: [...VISITS_KEY, 'window', from, to] as const,
    queryFn: ({ signal }) => api.visits.list({ from, to }, signal),
    staleTime: 25_000,
    // Same cadence as the board's order poll — two dispatchers stay in step.
    refetchInterval: 30_000,
  });
}

/** All visits on one order, soonest first. */
export function useOrderVisits(orderId: number | null) {
  return useQuery({
    queryKey: [...VISITS_KEY, 'order', orderId] as const,
    queryFn: ({ signal }) => api.visits.list({ orderId: orderId! }, signal),
    enabled: orderId !== null,
    staleTime: 30_000,
  });
}

/** A tech's own upcoming visits (as visit lead or helper), from `from` onward. */
export function useTechVisits(techId: string | null, from: string) {
  return useQuery({
    queryKey: [...VISITS_KEY, 'tech', techId, from] as const,
    queryFn: ({ signal }) => api.visits.list({ techId: techId!, from }, signal),
    enabled: techId !== null,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

/** Create / update / remove, each refreshing every visits query. */
export function useVisitMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: VISITS_KEY });
  const create = useMutation<JobVisit[], ApiError, CreateJobVisitBody>({
    mutationFn: (body) => api.visits.create(body),
    onSuccess: invalidate,
  });
  const update = useMutation<JobVisit, ApiError, { id: string; patch: UpdateJobVisitBody }>({
    mutationFn: ({ id, patch }) => api.visits.update(id, patch),
    onSettled: invalidate,
  });
  const remove = useMutation<void, ApiError, string>({
    mutationFn: (id) => api.visits.remove(id),
    onSuccess: invalidate,
  });
  return { create, update, remove };
}

/** A visit's start as a fractional clock hour ("08:30" → 8.5), like `Assignment.startHour`. */
export function visitStartHour(visit: Pick<JobVisit, 'startTime'>): number {
  return parseStoredStart(visit.startTime) ?? 8;
}

/**
 * A tech's My Day list with their extra visits folded in.
 *
 * Each visit becomes a copy of its order re-dated to the visit (date, start,
 * length), so the existing grouping, cards and start flow need no special
 * case. Rules:
 *   - a finished job's remaining visits don't show (it's done);
 *   - the order's own (first) visit is dropped once its day has passed IF a
 *     visit is still to come — otherwise a three-day job would sit under
 *     "Overdue" from day two;
 *   - `visitOnly` orders are ones the tech is on through a visit alone (not
 *     the order's own crew): only their visit copies show, never the first
 *     visit, which belongs to someone else;
 *   - one card per order per day.
 */
export function withVisitCards(
  assigned: ReadonlyArray<SalesOrderSummary>,
  visitOnly: ReadonlyArray<SalesOrderSummary>,
  visits: ReadonlyArray<JobVisit>,
  todayIso: string,
): SalesOrderSummary[] {
  const byId = new Map<number, SalesOrderSummary>();
  for (const o of visitOnly) byId.set(o.spireId, o);
  for (const o of assigned) byId.set(o.spireId, o);

  const copies: SalesOrderSummary[] = [];
  const upcoming = new Set<number>();
  const seen = new Set<string>();
  for (const o of assigned) {
    const a = readAssignment(o.udf ?? {});
    if (a) seen.add(`${o.spireId}:${a.date}`);
  }
  for (const v of visits) {
    if (v.date < todayIso) continue;
    const order = byId.get(v.spireSalesOrderId);
    if (!order || readCompletedAt(order.udf ?? {})) continue;
    upcoming.add(order.spireId);
    const key = `${order.spireId}:${v.date}`;
    if (seen.has(key)) continue;
    seen.add(key);
    copies.push({
      ...order,
      udf: {
        ...(order.udf ?? {}),
        [FOP_UDF.date]: v.date,
        [FOP_UDF.start]: v.startTime,
        [FOP_UDF.duration]: formatStoredDuration(v.durationHours),
      },
    });
  }

  const firsts = assigned.filter((o) => {
    const a = readAssignment(o.udf ?? {});
    return !(a && a.date < todayIso && upcoming.has(o.spireId));
  });
  return [...firsts, ...copies];
}
